import {
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from "react";

import { cn } from "~/lib/utils";

import {
    DPMP_MAP_DEFAULT_SIZE,
    DPMP_ZONE_LABELS,
    computeDpmpMapLayout,
    estimateTextWidth,
    wrapPartName,
    type DpmpMapEdgeInput,
    type DpmpMapLayout,
    type DpmpMapNode,
    DPMP_MAP_TUNING,
    type DpmpMapContext,
    type DpmpMapNodeInput,
    type DpmpMapSpoke,
    describeRelationship,
} from "../lib/dpmp-parts-map-layout";

// AIDEV-NOTE: SVG renderer for the DPMP® Parts Map (layout: ../lib/dpmp-parts-map-layout).
// Two modes (Adam, Sep 2026 — her paper format drowns at 80+ parts):
//   overview — the static card on /parts: rings, avatars, names, NO spokes, no hover; the
//              whole map is one button that opens the full-screen detail view.
//   detail   — full screen: spokes (toggleable, labels toggleable), hover reveal of the
//              related parts, click a part to open it.
// The component MEASURES its container and lays out in pixel space (viewBox = rendered
// px), so a bigger canvas spaces parts out rather than scaling everything up. Ground is
// cream; the outer circle is white with a soft shadow over it. Otherwise faithful to
// Michelle Glass's format: three rings, parts as circles in their zone, spokes with the
// related part's name set ALONG the spoke, clouds on the border, Self as the white space.

export interface DpmpPartsMapDisplay {
    showSpokes: boolean;
    showSpokeLabels: boolean;
    showNames: boolean;
}

export const DEFAULT_DPMP_MAP_DISPLAY: DpmpPartsMapDisplay = {
    showSpokes: true,
    showSpokeLabels: true,
    showNames: true,
};

interface DpmpPartsMapProps {
    nodes: DpmpMapNodeInput[];
    edges: DpmpMapEdgeInput[];
    mode: "overview" | "detail";
    display?: Partial<DpmpPartsMapDisplay>;
    /** detail: a part was clicked. */
    onSelectPart?: (partId: string) => void;
    /** overview: the map was clicked. */
    onActivate?: () => void;
    className?: string;
    /** Fixed render size in px (server renders, tests); measured from the container when omitted. */
    size?: number;
    /**
     * Draw a real line between every connected pair, always.
     * AIDEV-NOTE: On a scoped map (one part and its direct relations) the connections ARE
     * the content and there are few enough to draw honestly. Spokes exist because the
     * whole-system map cannot draw 50 crossing lines; that constraint does not apply here,
     * and a drawn line says "these two" far better than a stub plus a name.
     */
    connectors?: boolean;
    /** Avatars never grow past this — overrides the per-mode default. */
    maxNodeRadius?: number;
    /** Cap on characters per name-pill line — pill width is what collides. */
    nameMaxChars?: number;
    /** Selects the layout tuning preset (DPMP_MAP_TUNING). Defaults to the system map. */
    context?: DpmpMapContext;
}

/** Avatars stay small in the card so the overview reads as a map, not a grid of faces. */
// AIDEV-NOTE: Deliberately smaller than the avatars can be drawn. Node spacing scales
// with this radius, so trimming it buys distance between parts — and therefore readable
// names and relation labels — out of the same geometry rather than a bigger canvas.
const MAX_NODE_RADIUS: Record<DpmpPartsMapProps["mode"], number> = {
    overview: 22,
    detail: 30,
};

/** Arrowhead geometry for directed relationships, px. */
const ARROW_INSET = 3;
const ARROW_LENGTH = 9;
const ARROW_HALF_WIDTH = 4;

/** Downward bias of the name pill relative to the avatar's bottom edge, px. */
const PILL_DROP = 4;

function truncate(text: string, max: number): string {
    return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}

/** Cloud outline normalised to a ~44-unit width, scaled by the node radius. */
const CLOUD_PATH =
    "M-22,4 C-30,4 -30,-8 -20,-8 C-20,-20 -4,-22 0,-12 C6,-22 22,-16 18,-6 C28,-6 28,8 18,8 Z";

// AIDEV-NOTE: Zone colour coding (Adam, 2026-09-09): exiles PLUM, managers FOREST (unchanged),
// firefighters RED. The red is the palette's existing `brand-danger` scale — it is the system's
// only true red and is used here purely as a map zone colour, NOT as a danger/error signal.
// Firefighters were terra-600 and read as orange beside the exiles' terra-500.
// All three of these maps plus dpmp-parts-map-legend.tsx's swatches move together.

/**
 * A drawn relationship between two parts: the line, an arrowhead when the relationship
 * points, and the word set on the line itself.
 *
 * AIDEV-NOTE: The label sits at the midpoint on a white plate, and is flipped upright when
 * the line runs right-to-left so text is never upside down. The arrow is drawn as an
 * explicit polygon rather than an SVG marker so it inherits `opacity` with the line —
 * markers are rendered in a separate paint and would stay bright while the line faded.
 */
function RelationshipLine({
    from,
    to,
    type,
    lit,
    dashed,
    fontSize,
    showLabel,
    fromSource = true,
    nodeRadius = 0,
}: {
    from: { x: number; y: number };
    to: { x: number; y: number };
    type: string;
    lit: boolean;
    dashed?: boolean;
    fontSize: number;
    showLabel: boolean;
    /** False when `from` is the edge's TARGET — the arrow then points the other way. */
    fromSource?: boolean;
    /** Node radius, so the arrowhead lands on the perimeter instead of under the avatar. */
    nodeRadius?: number;
}) {
    const { label, directed } = describeRelationship(type);
    // AIDEV-NOTE: The arrow follows the EDGE, not the end we happened to render from.
    const tail = fromSource ? from : to;
    const head = fromSource ? to : from;
    const opacity = lit ? (dashed ? 0.7 : 0.75) : 0.06;
    const midX = (from.x + to.x) / 2;
    const midY = (from.y + to.y) / 2;

    let angle = (Math.atan2(to.y - from.y, to.x - from.x) * 180) / Math.PI;
    if (angle > 90 || angle < -90) angle += 180;

    // Arrowhead sits on the target's PERIMETER — inside the radius it is covered by the
    // avatar and the direction marker is invisible.
    const dx = head.x - tail.x;
    const dy = head.y - tail.y;
    const len = Math.hypot(dx, dy) || 1;
    const ux = dx / len;
    const uy = dy / len;
    const tipX = head.x - ux * (nodeRadius + ARROW_INSET);
    const tipY = head.y - uy * (nodeRadius + ARROW_INSET);
    const baseX = tipX - ux * ARROW_LENGTH;
    const baseY = tipY - uy * ARROW_LENGTH;
    const arrow = `${tipX},${tipY} ${baseX - uy * ARROW_HALF_WIDTH},${
        baseY + ux * ARROW_HALF_WIDTH
    } ${baseX + uy * ARROW_HALF_WIDTH},${baseY - ux * ARROW_HALF_WIDTH}`;

    return (
        <g className="transition-opacity" opacity={opacity}>
            <line
                x1={from.x}
                y1={from.y}
                x2={to.x}
                y2={to.y}
                className={
                    dashed
                        ? "stroke-brand-forest-500"
                        : "stroke-brand-forest-400"
                }
                strokeWidth={1.2}
                strokeDasharray={dashed ? "4 4" : undefined}
            />
            {directed ? (
                <polygon points={arrow} className="fill-brand-forest-500" />
            ) : null}
            {showLabel ? (
                <g transform={`translate(${midX} ${midY}) rotate(${angle})`}>
                    <rect
                        x={-(label.length * fontSize * 0.29) / 1}
                        y={-fontSize * 0.72}
                        width={label.length * fontSize * 0.58}
                        height={fontSize * 1.44}
                        rx={fontSize * 0.4}
                        className="fill-white"
                    />
                    <text
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize={fontSize}
                        className="fill-brand-forest-700"
                    >
                        {label}
                    </text>
                </g>
            ) : null}
        </g>
    );
}

const ZONE_FILL: Record<DpmpMapNode["zone"], string> = {
    exile: "fill-brand-plum-100",
    manager: "fill-brand-forest-100",
    firefighter: "fill-brand-danger-50",
    "protector-boundary": "fill-white",
    "unknown-boundary": "fill-white",
    guide: "fill-white",
    "unattached-burden": "fill-white",
};

const ZONE_STROKE: Record<DpmpMapNode["zone"], string> = {
    exile: "stroke-brand-plum-600",
    manager: "stroke-brand-forest-600",
    firefighter: "stroke-brand-danger-600",
    "protector-boundary": "stroke-brand-forest-500",
    "unknown-boundary": "stroke-brand-forest-500",
    guide: "stroke-brand-forest-400",
    "unattached-burden": "stroke-brand-forest-400",
};

// AIDEV-NOTE: The name pill is filled with the part's own ring colour, so the label itself
// carries the zone reading — keep in lockstep with ZONE_STROKE above.
const ZONE_PILL_FILL: Record<DpmpMapNode["zone"], string> = {
    exile: "fill-brand-plum-600",
    manager: "fill-brand-forest-600",
    firefighter: "fill-brand-danger-600",
    "protector-boundary": "fill-brand-forest-500",
    "unknown-boundary": "fill-brand-forest-500",
    guide: "fill-brand-forest-400",
    "unattached-burden": "fill-brand-forest-400",
};

/** Rendered square size of the container, in px; the default until measured. */
function useSquareSize(ref: React.RefObject<HTMLDivElement | null>): number {
    const [size, setSize] = useState(DPMP_MAP_DEFAULT_SIZE);
    useEffect(() => {
        const el = ref.current;
        if (!el || typeof ResizeObserver === "undefined") return;
        const measure = () => {
            const rect = el.getBoundingClientRect();
            const next = Math.round(Math.min(rect.width, rect.height));
            if (next > 0) setSize(next);
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [ref]);
    return size;
}

function ringPath(center: number, radius: number): string {
    // AIDEV-NOTE: Starts at the left and sweeps clockwise so a label set along it reads
    // upright across the top.
    return `M ${center - radius},${center} A ${radius},${radius} 0 1,1 ${
        center + radius
    },${center} A ${radius},${radius} 0 1,1 ${center - radius},${center}`;
}

function Spoke({
    spoke,
    nodeId,
    index,
    nodeRadius,
    fontSize,
    labelled,
    dimmed,
}: {
    spoke: DpmpMapSpoke;
    nodeId: string;
    index: number;
    nodeRadius: number;
    fontSize: number;
    labelled: boolean;
    dimmed: boolean;
}) {
    const gap = 3;
    const length = labelled ? spoke.length : Math.min(spoke.length, 22);
    // AIDEV-NOTE: The spoke is drawn in a frame rotated to its angle. A spoke pointing left
    // would set its label upside-down, so that frame is rotated by angle+180° and the spoke
    // runs along −x with the text end-anchored at the node — it still reads left-to-right
    // and hugs the node. No <textPath>: that clipped the START of long names on end-anchored
    // labels; here names are ellipsised at the end, so the first words always survive.
    const pointsLeft = Math.cos(spoke.angle) < -0.05;
    const deg =
        ((pointsLeft ? spoke.angle + Math.PI : spoke.angle) * 180) / Math.PI;
    const dir = pointsLeft ? -1 : 1;
    const inner = nodeRadius + gap;
    const outer = inner + length;
    const labelHeight = fontSize + 5;
    const rectX = dir === 1 ? inner : -outer;

    return (
        <g
            transform={`rotate(${deg.toFixed(2)})`}
            className={cn("transition-opacity", dimmed && "opacity-[0.06]")}
            data-testid={`dpmp-spoke-${nodeId}-${index}`}
        >
            {labelled ? (
                // Opaque backing so the name stays legible over rings, other spokes and
                // avatars.
                <rect
                    x={rectX}
                    y={-labelHeight}
                    width={length}
                    height={labelHeight + 2}
                    rx={2}
                    className="fill-white"
                    opacity={0.94}
                />
            ) : null}
            <line
                x1={dir * inner}
                y1={0}
                x2={dir * outer}
                y2={0}
                className="stroke-brand-forest-600"
                strokeWidth={1.1}
                strokeLinecap="round"
                opacity={0.85}
            />
            {labelled ? (
                <text
                    x={dir * (inner + 3)}
                    y={-3}
                    fontSize={fontSize}
                    textAnchor={pointsLeft ? "end" : "start"}
                    className="fill-brand-forest-800"
                >
                    {spoke.mark ? (
                        <>
                            {spoke.label.slice(0, -4)}
                            <tspan fontWeight={700}>{` (${spoke.mark})`}</tspan>
                        </>
                    ) : (
                        spoke.label
                    )}
                </text>
            ) : null}
        </g>
    );
}

function MapNode({
    uid,
    node,
    layout,
    display,
    interactive,
    active,
    related,
    dimmed,
    activeId,
    onHover,
    onSelect,
}: {
    uid: string;
    node: DpmpMapNode;
    layout: DpmpMapLayout;
    display: DpmpPartsMapDisplay;
    interactive: boolean;
    active: boolean;
    related: boolean;
    dimmed: boolean;
    activeId: string | null;
    onHover: (id: string | null) => void;
    onSelect?: (id: string) => void;
}) {
    const clipId = `${uid}-clip-${node.id}`;
    const r = node.radius;
    const emphasised = active || related;
    const zoneLabel = DPMP_ZONE_LABELS[node.zone];

    const interactiveProps = interactive
        ? {
              role: "link" as const,
              tabIndex: 0,
              "aria-label": `${node.name} — ${zoneLabel}. Open part.`,
              onMouseEnter: () => onHover(node.id),
              onMouseLeave: () => onHover(null),
              onFocus: () => onHover(node.id),
              onBlur: () => onHover(null),
              onClick: (event: React.MouseEvent) => {
                  event.stopPropagation();
                  onSelect?.(node.id);
              },
              onKeyDown: (event: React.KeyboardEvent) => {
                  if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onSelect?.(node.id);
                  }
              },
          }
        : {};

    return (
        <g
            transform={`translate(${node.x} ${node.y})`}
            className={cn(
                "outline-none transition-opacity",
                interactive && "cursor-pointer",
                // Unrelated parts recede hard so the hovered constellation is what reads.
                dimmed && "opacity-[0.08]",
            )}
            data-testid="dpmp-parts-map-node"
            data-zone={node.zone}
            {...interactiveProps}
        >
            {/* AIDEV-NOTE: One text child only — SVG <title> can't take fragments
                without a hydration mismatch. */}
            <title>{`${node.name} · ${zoneLabel}`}</title>
            {/* AIDEV-NOTE: While a part is hovered, a RELATED part draws only the spoke
                back to it. Showing a neighbour's other spokes answers a question nobody
                asked and is what made the hover read as noise rather than a constellation. */}
            {display.showSpokes
                ? (related && activeId
                      ? node.spokes.filter(
                            (spoke) => spoke.targetId === activeId,
                        )
                      : node.spokes
                  ).map((spoke, index) => (
                      <Spoke
                          key={`${spoke.targetId}-${spoke.type}`}
                          spoke={spoke}
                          nodeId={node.id}
                          index={index}
                          nodeRadius={node.cloud ? r * 1.1 : r}
                          fontSize={layout.spokeFontSize}
                          labelled={display.showSpokeLabels}
                          dimmed={dimmed}
                      />
                  ))
                : null}
            {node.cloud ? (
                <path
                    d={CLOUD_PATH}
                    transform={`scale(${r / 20})`}
                    className={cn(ZONE_FILL[node.zone], ZONE_STROKE[node.zone])}
                    strokeWidth={(emphasised ? 2.6 : 1.6) / (r / 20)}
                    strokeDasharray={
                        node.dashed
                            ? `${4 / (r / 20)} ${3 / (r / 20)}`
                            : undefined
                    }
                    vectorEffect="non-scaling-stroke"
                />
            ) : (
                <circle
                    r={r}
                    className={cn(ZONE_FILL[node.zone], ZONE_STROKE[node.zone])}
                    strokeWidth={emphasised ? 3 : 1.5}
                    strokeDasharray={node.dashed ? "5 3" : undefined}
                />
            )}
            {node.imageUrl && !node.cloud ? (
                <>
                    <clipPath id={clipId}>
                        <circle r={r - 1.5} />
                    </clipPath>
                    <image
                        href={node.imageUrl}
                        x={-r}
                        y={-r}
                        width={r * 2}
                        height={r * 2}
                        clipPath={`url(#${clipId})`}
                        preserveAspectRatio="xMidYMid slice"
                    />
                </>
            ) : null}
            {emphasised ? (
                <circle
                    r={r + 4}
                    fill="none"
                    className="stroke-brand-forest-700"
                    strokeWidth={1.2}
                    strokeDasharray="2 3"
                />
            ) : null}
            {display.showNames ? (
                <NamePill node={node} layout={layout} emphasised={emphasised} />
            ) : null}
        </g>
    );
}

// AIDEV-NOTE: Names ride ON the avatar as a pill rather than beside it. Radial labels had
// to reserve clear space around every node, which is space the parts themselves could be
// using; the pill costs only the image it covers (an accepted trade) and its position is
// deterministic, so a name can never drift away from the part it belongs to. It straddles
// the avatar's BOTTOM edge so the face stays readable above it, and it is filled with the
// part's own ring colour so the label doubles as the zone cue.
function NamePill({
    node,
    layout,
    emphasised,
}: {
    node: DpmpMapNode;
    layout: DpmpMapLayout;
    emphasised: boolean;
}) {
    const lines = wrapPartName(node.name, layout.nameMaxChars);
    const lineHeight = layout.nameFontSize + 1.5;
    const height = lines.length * lineHeight + 6;
    const width =
        Math.max(
            ...lines.map((line) =>
                estimateTextWidth(line, layout.nameFontSize),
            ),
        ) + 12;
    // Straddle the avatar's bottom edge, biased slightly downward so more of the face
    // stays visible above the pill.
    const top = node.radius - height / 2 + PILL_DROP;
    return (
        <g>
            <rect
                x={-width / 2}
                y={top}
                width={width}
                height={height}
                rx={height / 2}
                className={ZONE_PILL_FILL[node.zone]}
            />
            {lines.map((line, i) => (
                <text
                    key={line + i}
                    x={0}
                    y={top + 3 + lineHeight * (i + 0.5)}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={layout.nameFontSize}
                    fontWeight={emphasised ? 700 : 600}
                    className="fill-white"
                >
                    {line}
                </text>
            ))}
        </g>
    );
}

export function DpmpPartsMap({
    nodes,
    edges,
    mode,
    display: displayOverrides,
    onSelectPart,
    onActivate,
    className,
    size: sizeOverride,
    connectors = false,
    maxNodeRadius,
    context = "system",
    nameMaxChars,
}: DpmpPartsMapProps) {
    // AIDEV-NOTE: Two maps can be mounted at once (card + full-screen overlay); every
    // fragment id is prefixed per instance so filters, clips and textPaths never cross.
    const uid = `dpmp-${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
    const containerRef = useRef<HTMLDivElement>(null);
    const measured = useSquareSize(containerRef);
    const size = sizeOverride ?? measured;
    const layout = useMemo(
        () =>
            computeDpmpMapLayout(nodes, edges, {
                size,
                maxNodeRadius: maxNodeRadius ?? MAX_NODE_RADIUS[mode],
                context,
                nameMaxChars,
            }),
        [nodes, edges, size, mode, maxNodeRadius, nameMaxChars, context],
    );

    const autoHideRatio = DPMP_MAP_TUNING[context].autoHideNamesCollisionRatio;
    const [activeId, setActiveId] = useState<string | null>(null);
    const interactive = mode === "detail";
    // AIDEV-NOTE: The overview never draws spokes, whatever the caller passes — that is the
    // whole point of the mode.
    const display: DpmpPartsMapDisplay = {
        ...DEFAULT_DPMP_MAP_DISPLAY,
        ...displayOverrides,
        showSpokes: interactive && (displayOverrides?.showSpokes ?? true),
        // AIDEV-NOTE: The overview keeps its names. They used to be hidden when the old
        // radial placement collided too often; pills are pinned to their avatar and the
        // layout already steps the avatar size down when too many overlap, so the card and
        // the full-screen view now show the same thing.
        // AIDEV-NOTE: On a dense system map names START hidden and the reader turns them
        // on — same contract the legend already states for relations. An explicit
        // showNames override always wins, so the panel's toggle still works.
        showNames:
            displayOverrides?.showNames ??
            (autoHideRatio === null ||
                layout.labelCollisions <= layout.nodes.length * autoHideRatio),
    };

    const nodeById = useMemo(
        () => new Map(layout.nodes.map((node) => [node.id, node] as const)),
        [layout.nodes],
    );
    const activeNode =
        interactive && activeId ? nodeById.get(activeId) : undefined;
    const relatedIds = useMemo(
        () => new Set(activeNode?.spokes.map((spoke) => spoke.targetId) ?? []),
        [activeNode],
    );
    /**
     * Is this part part of the hovered constellation — the active part itself, or one it
     * has a relation to? Single definition, used both to dim NODES and to dim the
     * connector lines between them, so the two can never disagree.
     */
    const isInConstellation = useCallback(
        (id: string) => id === activeId || relatedIds.has(id),
        [activeId, relatedIds],
    );

    const { center, rings } = layout;
    const ringLabelProps = {
        fontSize: 10.5,
        fontStyle: "italic",
        letterSpacing: 1.5,
        className: "fill-brand-forest-500 uppercase",
        // AIDEV-NOTE: Positive dy sets the label just INSIDE its ring line, so each name
        // sits in the area it describes (Adam, Sep 2026).
        dy: 13,
    } as const;
    const activatable = mode === "overview" && !!onActivate;

    return (
        // AIDEV-NOTE: The white ground and its shadow live on THIS div, not inside the
        // SVG. An feDropShadow on an in-SVG rect is clipped by the viewBox, so the shadow
        // was cut off at the edges. Using the same `shadow-card` as the part cards keeps
        // the surfaces consistent and lets the shadow cast outside the card.
        <div
            ref={containerRef}
            className={cn(
                "h-full w-full rounded-[28px] bg-white shadow-card",
                className,
            )}
            data-testid="dpmp-parts-map-container"
        >
            <svg
                viewBox={`0 0 ${layout.size} ${layout.size}`}
                width="100%"
                height="100%"
                className={cn(
                    "select-none font-sans",
                    activatable && "cursor-zoom-in",
                )}
                role={activatable ? "button" : "img"}
                tabIndex={activatable ? 0 : undefined}
                aria-label={
                    activatable
                        ? "DPMP Parts Map — open full screen"
                        : "DPMP Parts Map"
                }
                onClick={activatable ? onActivate : undefined}
                onKeyDown={
                    activatable
                        ? (event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  onActivate?.();
                              }
                          }
                        : undefined
                }
                data-testid="dpmp-parts-map-svg"
                data-mode={mode}
            >
                <defs>
                    <path
                        id={`${uid}-ring-exile`}
                        d={ringPath(center, rings.exile)}
                    />
                    <path
                        id={`${uid}-ring-manager`}
                        d={ringPath(center, rings.manager)}
                    />
                    {/* AIDEV-NOTE: Never stroked — firefighters are uncontained. This path
                        exists only to curve their zone label, so it rides the middle of
                        their lane rather than a boundary that no longer exists. */}
                    <path
                        id={`${uid}-ring-outer`}
                        d={ringPath(center, (rings.manager + rings.outer) / 2)}
                    />
                </defs>

                {/* Two rings only — the inner is the heavy line, as on Michelle's blank map. */}
                <circle
                    cx={center}
                    cy={center}
                    r={rings.manager}
                    fill="none"
                    className="stroke-brand-forest-800"
                    strokeWidth={1.3}
                />
                <circle
                    cx={center}
                    cy={center}
                    r={rings.exile}
                    fill="none"
                    className="stroke-brand-forest-800"
                    strokeWidth={2.2}
                />

                {/* Zone names set along the ring lines, upper-left, just inside the zone
                    each describes. */}
                <text {...ringLabelProps}>
                    <textPath href={`#${uid}-ring-exile`} startOffset="12%">
                        Exiles
                    </textPath>
                </text>
                <text {...ringLabelProps}>
                    <textPath href={`#${uid}-ring-manager`} startOffset="12%">
                        Managers
                    </textPath>
                </text>
                <text {...ringLabelProps}>
                    <textPath href={`#${uid}-ring-outer`} startOffset="12%">
                        Firefighters
                    </textPath>
                </text>
                <text
                    x={14}
                    y={layout.size - 14}
                    fontSize={10.5}
                    fontStyle="italic"
                    letterSpacing={1.5}
                    className="fill-brand-forest-500 uppercase"
                >
                    Self — around all parts
                </text>

                {/* Permanent connectors — every edge, drawn once (deduped by pair). */}
                {connectors
                    ? (() => {
                          // Dedupe by unordered pair — a relation may produce a spoke on
                          // one side only, so filtering on id order would drop it.
                          const drawn = new Set<string>();
                          return layout.nodes.flatMap((node) =>
                              node.spokes.map((spoke) => {
                                  const target = nodeById.get(spoke.targetId);
                                  if (!target) return null;
                                  const pair = [node.id, spoke.targetId]
                                      .sort()
                                      .join("|");
                                  if (drawn.has(pair)) return null;
                                  drawn.add(pair);
                                  // AIDEV-NOTE: A connector recedes with the parts it joins.
                                  // It stays lit only while BOTH ends are in the hovered
                                  // constellation (the active part or one related to it) —
                                  // the same test MapNode uses for `dimmed`, so a line never
                                  // stays bright between two parts that have faded out.
                                  const lit =
                                      !activeNode ||
                                      (isInConstellation(node.id) &&
                                          isInConstellation(spoke.targetId));
                                  return (
                                      // AIDEV-NOTE: On a scoped map the connections ARE
                                      // the content, so the relationship is named on every
                                      // line — Adam/Michelle, 2026-09-09: with a line
                                      // already joining the two parts, repeating the part
                                      // name adds nothing, but "protects →" does.
                                      <RelationshipLine
                                          key={`link-${node.id}-${spoke.targetId}-${spoke.type}`}
                                          from={node}
                                          to={target}
                                          type={spoke.type}
                                          lit={lit}
                                          fromSource={spoke.fromSource}
                                          // The node the arrow LANDS on, whose radius
                                          // differs for clouds and boundary parts.
                                          nodeRadius={
                                              spoke.fromSource
                                                  ? target.radius
                                                  : node.radius
                                          }
                                          fontSize={layout.spokeFontSize}
                                          showLabel={lit}
                                      />
                                  );
                              }),
                          );
                      })()
                    : null}

                {/* Hover reveal: faint lines from the active part to each related part. */}
                {/* AIDEV-NOTE: On the whole-system map the relationship words would be
                    unreadable across 50 crossing lines, so they appear ONLY here — on the
                    hovered part's own relations (Adam/Michelle, 2026-09-09: "we'll probably
                    only want to render those things when those parts are hovered"). */}
                {activeNode
                    ? activeNode.spokes.map((spoke) => {
                          const target = nodeById.get(spoke.targetId);
                          if (!target) return null;
                          return (
                              <RelationshipLine
                                  key={`${spoke.targetId}-${spoke.type}`}
                                  from={activeNode}
                                  to={target}
                                  type={spoke.type}
                                  lit
                                  dashed
                                  fromSource={spoke.fromSource}
                                  nodeRadius={
                                      spoke.fromSource
                                          ? target.radius
                                          : activeNode.radius
                                  }
                                  fontSize={layout.spokeFontSize}
                                  showLabel={!connectors}
                              />
                          );
                      })
                    : null}

                {layout.nodes.map((node) => (
                    <MapNode
                        key={node.id}
                        uid={uid}
                        node={node}
                        layout={layout}
                        display={display}
                        interactive={interactive}
                        active={node.id === activeId}
                        related={relatedIds.has(node.id)}
                        activeId={activeId}
                        dimmed={!!activeNode && !isInConstellation(node.id)}
                        onHover={setActiveId}
                        onSelect={onSelectPart}
                    />
                ))}
            </svg>
        </div>
    );
}
